import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
  get_interpreter_by_id,
  get_interpreter_queues,
  get_interpreter_tiers,
} from "../../api/InterpreterApi";
import { Loader2 } from "lucide-react";
import { GlobalContext } from "../../App";
import countries from "../../assets/countries.json";
import { useAuth } from "../../context/AuthContext";

const InterpreterInfo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [interpreter, setInterpreter] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [error, setError] = useState(null);
  const { languages } = useContext(GlobalContext);
  const { user } = useAuth();
  useEffect(() => {
    const fetchInterpreter = async () => {
      try {
        setLoading(true);
        const response = await get_interpreter_by_id(id);
        const tiersResponse = await get_interpreter_tiers(id);
        const queuesResponse = await get_interpreter_queues(
          response.data.z_user_id
        );
        setTiers(tiersResponse.data);
        setQueues(queuesResponse.data);
        if (response && response.data) {
          const interpreterData = response.data;
          // Set the languages from the response
          if (response.languages) {
            setSelectedLanguages(response.languages);
            // Update the interpreter object with languages from response
            interpreterData.languages = response.languages;
          }
          // Set interpreter_home_number if it exists in the response
          if (response.interpreter_home_number) {
            interpreterData.interpreter_home_number =
              response.interpreter_home_number;
          }
          setInterpreter(interpreterData);
        } else {
          setError("Failed to load interpreter data");
        }
      } catch (err) {
        console.error("Error fetching interpreter:", err);
        setError("An error occurred while fetching interpreter data");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInterpreter();
    }
  }, [id]);

  // Convert the array of tier rates to the object format for display
  const mapTiersToDisplayFormat = () => {
    const tierRatesObj = {
      1: { voice_rate: "", video_rate: "" },
      2: { voice_rate: "", video_rate: "" },
      3: { voice_rate: "", video_rate: "" },
      4: { voice_rate: "", video_rate: "" },
    };
    // If tiers array exists, map each tier to the corresponding object property
    if (tiers && tiers.length > 0) {
      tiers.forEach((tier) => {
        if (tier.interpreter_tier >= 1 && tier.interpreter_tier <= 4) {
          tierRatesObj[tier.interpreter_tier] = {
            voice_rate: tier.voice_rate || "",
            video_rate: tier.video_rate || "",
          };
        }
      });
    }
    return tierRatesObj;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading interpreter data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
        <div className="mt-4">
          <Button onClick={() => navigate("/admin/interpreters")}>
            Back to Interpreters List
          </Button>
        </div>
      </div>
    );
  }

  const tierRates = mapTiersToDisplayFormat();
  const interpreterLanguages =
    selectedLanguages || interpreter?.interpreter_languages || [];
  const interpreterCountry = interpreter?.interpreter_country || "";

  // Get country display name
  const getCountryDisplay = (countryCode) => {
    if (!countryCode) return "Not specified";
    const country = countries.find((c) => c.cca2 === countryCode);
    return country ? `${country.name} ${country.flag}` : countryCode;
  };

  return (
    <div className="mx-auto pb-10">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate("/admin")}>
              Admin
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate("/admin/interpreters")}>
              Interpreters
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Interpreter Information</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <CardHeader>
              <CardTitle>Interpreter Information</CardTitle>
              <CardDescription>
                View interpreter details and information.
              </CardDescription>
            </CardHeader>
          </div>
          {interpreter?.interpreter_tier == "4" && (
            <div className="flex items-center gap-2 mr-6">
              <span className="inline-flex items-center rounded-md bg-purple-100 px-3 py-2 font-medium text-purple-700 text-base">
                {"Tier 4"}
              </span>
            </div>
          )}
        </div>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_first_name || "Not specified"}
                </div>
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_last_name || "Not specified"}
                </div>
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_phone_number || "Not specified"}
                </div>
              </div>

              {/* Home Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Home Number
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_home_number || "Not specified"}
                </div>
              </div>

              {/* Interpreter ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Interpreter ID
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_special_id || "Not specified"}
                </div>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Country
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {getCountryDisplay(interpreterCountry)}
                </div>
              </div>

              {/* Video Rate */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.email || "Not specified"}
                </div>
              </div>

              {/* Video Rate */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Video Rate
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.video_rate
                    ? `$${interpreter.video_rate}`
                    : "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Address
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_address
                    ? interpreter.interpreter_address
                    : "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  City
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_city
                    ? interpreter.interpreter_city
                    : "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  State
                </label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_state
                    ? interpreter.interpreter_state
                    : "Not specified"}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Zip</label>
                <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                  {interpreter?.interpreter_zip
                    ? interpreter.interpreter_zip
                    : "Not specified"}
                </div>
              </div>
            </div>

            {/* Voice Rates Per Tier */}
            {user?.user_type !== "supervisor" && (
              <div className="mt-6">
                <h3 className="text-base font-semibold mb-4">
                  Voice Rates Per Tier
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Tier 1 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 1 Voice Rate ($/hr)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[1]?.voice_rate
                        ? `$${tierRates[1].voice_rate}`
                        : "Not specified"}
                    </div>
                  </div>

                  {/* Tier 2 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 2 Voice Rate ($/min)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[2]?.voice_rate
                        ? `$${tierRates[2].voice_rate}`
                        : "Not specified"}
                    </div>
                  </div>

                  {/* Tier 3 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 3 Voice Rate ($/min)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[3]?.voice_rate
                        ? `$${tierRates[3].voice_rate}`
                        : "Not specified"}
                    </div>
                  </div>

                  {/* Tier 4 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 4 Voice Rate ($/min)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[4]?.voice_rate
                        ? `$${tierRates[4].voice_rate}`
                        : "Not specified"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Video Rates Per Tier */}
            {user?.user_type !== "supervisor" && (
              <div className="mt-6">
                <h3 className="text-base font-semibold mb-4">
                  Video Rates Per Tier
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Tier 1 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 1 Video Rate ($/hr)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[1]?.video_rate
                        ? `$${tierRates[1].video_rate}`
                        : "Not specified"}
                    </div>
                  </div>

                  {/* Tier 2 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 2 Video Rate ($/min)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[2]?.video_rate
                        ? `$${tierRates[2].video_rate}`
                        : "Not specified"}
                    </div>
                  </div>

                  {/* Tier 3 Voice Rate */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Tier 3 Video Rate ($/min)
                    </label>
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                      {tierRates[3]?.video_rate
                        ? `$${tierRates[3].video_rate}`
                        : "Not specified"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Languages */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Languages
              </label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                {interpreterLanguages.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {interpreterLanguages.map((lang, index) => {
                      // Handle both object format and string format
                      const displayName =
                        typeof lang === "object" && lang !== null
                          ? lang.attribute_value
                          : languages.find((l) => l.code === lang)?.name ||
                            lang;

                      const key =
                        typeof lang === "object" && lang !== null
                          ? lang.attribute_value
                          : lang;

                      return (
                        <div
                          key={`${key}-${index}`}
                          className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md"
                        >
                          {displayName}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  "No languages specified"
                )}
              </div>
            </div>

            {/* Notes Section */}
            <div className="mt-6">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Notes
              </label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-md min-h-[100px]">
                {interpreter?.interpreter_notes ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: interpreter.interpreter_notes,
                    }}
                  />
                ) : (
                  "No notes available"
                )}
              </div>
            </div>

            {/* Tiers */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Tiers</label>
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
                {queues.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {queues.map((queue, index) => {
                      const displayName = queue.queue_name;

                      const key = queue.cc_queue_id;

                      return (
                        <div
                          key={`${key}-${index}`}
                          className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md"
                        >
                          {displayName}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  "No tiers specified"
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-end space-x-4 mt-6 border-t border-gray-200 shadow-lg   ">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/interpreters")}
              >
                Back to List
              </Button>
              <Button
                type="button"
                onClick={() => navigate(`/admin/interpreters/edit/${id}`)}
              >
                Edit Interpreter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InterpreterInfo;
